import * as React from 'react'
import { ReactElement } from 'react'
import { FieldArray, useFormikContext } from 'formik'
import { FileMutationDto } from '../../../../config/fileConfig'
import TierList from '../../Tier/TierList'
import { UserTierDto } from '../../../../config/finance/financeConfig'
import { UserTierListItem } from '../../Tier/UserTierListItem'
import UserTierFormContainer, { userTierValidationSchema } from '../../Tier/UserTierFormContainer'
import { useSettings } from '../../../../hooks/useSettings'
import { useTranslation } from 'react-i18next'
import { CommitEventType } from '../../../Input/FormattableNumberInput'
import MoneyInput from '../../../Input/MoneyInput'
import { Divider, Field } from '@fluentui/react-components'
import { RateFieldProps } from './RateFields'
import BillableCombobox from '../../../Combobox/BillableCombobox'
import FileSurchargeList from '../Surcharge/FileSurchargeList'
import useSurchargeTypes from '../../../../hooks/useSurchargeTypes'
import EnumCombobox from '../../../Combobox/EnumCombobox'
import { FileRateType } from '../../../../context/GlobalContext'

export function getDefaultTierValue(currency: string): UserTierDto {
    return {
        type: 'USER_TIER',
        purchaseRate: undefined,
        salesRate: '0.00',
        userId: undefined,
        currency: currency,
    }
}

export default function RateFieldsContents({ orientation }: RateFieldProps): ReactElement {
    const { t: translate } = useTranslation()
    const settings = useSettings()
    const { surchargeTypes } = useSurchargeTypes()

    const { initialValues, values, setFieldValue } = useFormikContext<FileMutationDto<UserTierDto>>()

    return (
        <>
            <Field label={translate('file.edit.form.finance.rates.type.label')} required orientation={'horizontal'}>
                <EnumCombobox<FileRateType>
                    required
                    entities={Object.values(FileRateType)}
                    labelKeyPrefix={'file.edit.form.finance.rates.type'}
                    placeholderKey={'file.edit.form.finance.rates.type.placeholder'}
                    selectedOptions={[values.fileRateType]}
                    onOptionSelect={(_, data) => {
                        // Only update these values if the toggle for tiers/rate has been switched
                        setFieldValue('fileRateType', data.selectedOptions?.[0]).then(() => {
                            switch (data.selectedOptions?.[0] as FileRateType) {
                                case FileRateType.File: {
                                    setFieldValue('rate', initialValues.rate ?? settings?.fileScaffold?.fileRate?.hourRate)
                                    setFieldValue('tiers', undefined)
                                    break
                                }
                                default: {
                                    setFieldValue('rate', undefined)
                                    setFieldValue('tiers', undefined)
                                }
                            }
                        })
                    }}
                />
            </Field>
            {FileRateType.UserTier === values.fileRateType && (
                <Field label={translate('file.edit.form.finance.tier')} required orientation={orientation}>
                    <FieldArray name="tiers">
                        {() => (
                            <TierList<FileMutationDto<UserTierDto>, UserTierDto>
                                labelPrefixKey={'file.edit.form.finance.rates.tier'}
                                defaultTierValue={() => getDefaultTierValue(values.currency)}
                                parentValues={values}
                                createListItem={(index: number, item: UserTierDto, editTier) => (
                                    <UserTierListItem index={index} item={item} editTier={editTier} />
                                )}
                                createTierFormContainer={props => <UserTierFormContainer {...props} />}
                                validationSchema={userTierValidationSchema}
                            />
                        )}
                    </FieldArray>
                </Field>
            )}
            {FileRateType.File === values.fileRateType && (
                <Field label={translate('file.edit.form.finance.rate')} required orientation={orientation}>
                    <MoneyInput
                        value={values?.rate}
                        currency={values.currency}
                        onCommit={(eventType: CommitEventType, value: string) => {
                            if (eventType !== CommitEventType.OnLoad) {
                                setFieldValue('rate', value)
                            }
                        }}
                    />
                </Field>
            )}
            <Divider />

            {surchargeTypes.length > 0 && (
                <>
                    <Field label={translate('file.edit.form.finance.surcharge.label')} required orientation={orientation}>
                        <FieldArray name="surcharges">{() => <FileSurchargeList />}</FieldArray>
                    </Field>
                    <Divider />
                </>
            )}

            <Field label={translate('base.billable.label')} required orientation={orientation}>
                <BillableCombobox
                    required
                    selectedOptions={[values.billable]}
                    onOptionSelect={(_, data) => setFieldValue('billable', data.selectedOptions?.[0])}
                />
            </Field>
        </>
    )
}
